import { notification, Typography } from 'antd'
import { useCallback } from 'react'
import connect from '../../../helpers/api/connect'
import {
    useCreateCalculationPositionMutation,
    useUpdateCalculationPositionMutation,
} from '../../../store/api/calculation-positions-api'
import { useCreateCalculationMutation } from '../../../store/api/calculations-api'
import Calculation from '../../../types/api/calculation'
import CalculationPosition from '../../../types/api/calculation-position'
import Shipment from '../../../types/api/shipment'
import ShipmentPosition from '../../../types/api/shipment-position'
import { StrapiCreate } from '../../../types/strapi/strapi'

const extractCreateCalculationPositionQueryArg = ({
    shipment,
    position,
}: {
    shipment: Shipment
    position: ShipmentPosition
}): StrapiCreate<CalculationPosition> => ({
    data: {
        comment: position.comment,
        copies: position.copies,
        floodsInAccumulation: position.floodsInAccumulation,
        withoutCutting: shipment.withoutCutting,
        stamp: shipment.withoutCutting ? undefined : connect(shipment.stamp),
        virtualStamp: shipment.withoutCutting
            ? {
                  width: shipment.virtualStamp?.width,
                  length: shipment.virtualStamp?.length,
                  floods: shipment.virtualStamp?.floods,
                  range: shipment.virtualStamp?.range,
                  roller: shipment.virtualStamp?.roller?.documentId
                      ? { set: [shipment.virtualStamp.roller.documentId] }
                      : undefined,
              }
            : undefined,
        material_type: connect(shipment.materialRequirements?.[0]?.material_type),
        whitewash: shipment.whitewash,
        cmyk: shipment.cmyk,
        pantones: shipment.pantones,
        ...(shipment.lacquers && {
            lacquers: shipment.lacquers.map(l => ({
                type: connect(l.type),
                name: l.name,
            })),
        }),
        shipmentType: shipment.shipmentType,
        sellingPrice: shipment.sellingPrice,
        additional_material_types: connect(
            (shipment.materialRequirements ?? [])
                .slice(1)
                .flatMap(mr => (mr.material_type ? [mr.material_type] : []))
        ),
        shipment_position: connect(position),
        machine: connect(shipment.machine),
        additional_machines: connect(shipment.additional_machines),
        additional_jobs: connect(shipment.additional_jobs),
        paintCostOf1000DurstWG: shipment.paintCostOf1000DurstWG,
        currencyRate: shipment.currencyRate,
        stampFloods: shipment.withoutCutting ? undefined : shipment.stampFloods,
        numberOfForms: shipment.numberOfForms,
    },
})

const getShipmentPositionAll = (rows: Shipment[]) =>
    rows.flatMap(shipment =>
        (shipment.shipment_positions ?? []).map(position => ({ shipment, position }))
    )

function isDigitalMachine(machine?: { name?: string } | null): boolean {
    const name = machine?.name ?? ''
    return name.includes('Durst') || name.startsWith('GF')
}

/** Один номер сборки на цифровую отгрузку с несколькими видами. У разных отгрузок клиента номера разные. */
function accumulationByShipmentDocumentId(
    couples: Array<{ shipment: Shipment }>
): Map<string, number> {
    const nextByClient = new Map<string, number>()
    const byShipment = new Map<string, number>()

    for (const { shipment } of couples) {
        if (byShipment.has(shipment.documentId)) continue
        const clientId = shipment.client?.documentId
        const positionCount = shipment.shipment_positions?.length ?? 0
        if (!clientId || !isDigitalMachine(shipment.machine) || positionCount < 2) continue

        const next = (nextByClient.get(clientId) ?? 0) + 1
        nextByClient.set(clientId, next)
        byShipment.set(shipment.documentId, next)
    }

    return byShipment
}

const extractCreateCalculationQueryArg = (
    clientDocumentId: string,
    calcPosDocIds: string[]
): StrapiCreate<Calculation> => ({
    data: {
        client: { connect: [clientDocumentId] },
        comment: 'Повтор заказа',
        calculation_positions: { connect: calcPosDocIds },
    },
})

export function useRepeatOrder() {
    const [createCalculationPosition, createCalculationPositionState] =
        useCreateCalculationPositionMutation()
    const [createCalculation, createCalculationState] = useCreateCalculationMutation()
    const [updateCalculationPosition] = useUpdateCalculationPositionMutation()

    const repeatOrder = useCallback(
        async (shipments: Shipment[]) => {
            const couples = getShipmentPositionAll(shipments)
            const promises = couples.map(couple =>
                createCalculationPosition(extractCreateCalculationPositionQueryArg(couple))
            )

            const results = await Promise.all(promises)
            const createdDocIds = results.map(res =>
                'data' in res && res.data ? res.data.data.documentId : undefined
            )
            // TODO: тут надо не фильтровать не созданные, а выводить ошибку, хотя ее можно и хуком
            const accumulationByShipment = accumulationByShipmentDocumentId(couples)
            const byClient: Record<string, string[]> = {}
            const positionUpdates: Array<{ docId: string; accumulation?: number }> = []
            couples.forEach((couple, i) => {
                const docId = createdDocIds[i]
                const clientId = couple.shipment.client?.documentId
                if (docId && clientId) {
                    byClient[clientId] ??= []
                    byClient[clientId].push(docId)
                    positionUpdates.push({
                        docId,
                        accumulation: accumulationByShipment.get(couple.shipment.documentId),
                    })
                }
            })
            const createCalcResults = await Promise.all(
                Object.entries(byClient).map(([clientId, ids]) =>
                    createCalculation(extractCreateCalculationQueryArg(clientId, ids))
                )
            )
            if (createCalcResults.every(res => 'data' in res && res.data)) {
                const plain = positionUpdates.filter(item => item.accumulation == null)
                const assembled = positionUpdates.filter(item => item.accumulation != null)

                await Promise.all(
                    plain.map(item =>
                        updateCalculationPosition({
                            docId: item.docId,
                            body: { data: {} },
                        })
                    )
                )
                // По очереди: пересчёт сборки суммирует тиражи только уже проставленных номеров.
                for (const item of assembled) {
                    await updateCalculationPosition({
                        docId: item.docId,
                        body: { data: { accumulation: item.accumulation } },
                    })
                }
                notification.success({
                    message: (
                        <div>
                            <p>Расчеты созданы</p>
                            <Typography.Link>Посмотреть</Typography.Link>
                        </div>
                    ),
                })
            }
        },
        [createCalculation, createCalculationPosition, updateCalculationPosition]
    )

    return {
        repeatOrder,
        isLoading: createCalculationPositionState.isLoading || createCalculationState.isLoading,
    }
}
